import * as XLSX from 'xlsx';

/** What the file input accepts: the desk fills the template in Excel as often as it exports a CSV. */
export const MARKETING_IMPORT_ACCEPT = '.csv,.xlsx,.xls';

/** The name the downloaded template is saved under. */
export const MARKETING_IMPORT_TEMPLATE_FILENAME = 'marketing-import-template.xlsx';

/**
 * Excel shows a whole number of 12 or more digits in a General cell as `4.42079E+11`, and the CSV takes that text,
 * so a phone typed into a number cell reached the label with its digits gone (SSD-7512). Such a cell is read back
 * with every digit, up to 15: Excel itself keeps no more than 15 significant digits, so a longer number (a 16-digit
 * waybill typed as a number) has already changed and stays in E notation, which reads as broken, rather than turning
 * into a plausible wrong number. A cell with its own format keeps the text it shows: `00000` is how a sheet keeps a
 * postcode's leading zeros, and dates and decimals read as written.
 */
const keepWholeNumbers = (sheet: XLSX.WorkSheet): XLSX.WorkSheet => {
  for (const [address, cell] of Object.entries(sheet)) {
    if (address.startsWith('!')) {
      continue;
    }
    const { t, v, z } = cell as XLSX.CellObject;
    if (
      t === 'n' &&
      typeof v === 'number' &&
      Number.isInteger(v) &&
      Math.abs(v) < 1e15 &&
      (z === undefined || z === 'General')
    ) {
      (cell as XLSX.CellObject).w = String(v);
    }
  }
  return sheet;
};

/**
 * A workbook is read back into the CSV the server parses, so both file kinds take one path and the
 * desk sees the same preview whichever it uploaded. Lives apart from the presentation helpers so
 * only the screens that read files pull `xlsx` into their bundle.
 */
export const readAsCsv = async (file: File): Promise<string> => {
  if (!/\.xlsx?$/i.test(file.name)) {
    return file.text();
  }
  // cellNF keeps each cell's number format, which is how a General cell is told apart from a zero-padded one.
  const book = XLSX.read(await file.arrayBuffer(), { type: 'array', cellNF: true });
  const firstSheet = book.SheetNames[0];
  const sheet = firstSheet === undefined ? undefined : book.Sheets[firstSheet];
  // An empty workbook parses to an empty file, which the preview then reports as zero rows.
  return sheet === undefined ? '' : XLSX.utils.sheet_to_csv(keepWholeNumbers(sheet));
};

/** Splits CSV text into records, honouring quoted cells that carry commas, quotes, or line breaks. */
export const parseCsv = (text: string): string[][] => {
  const records: string[][] = [];
  let record: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      record.push(cell);
      cell = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') {
        index += 1;
      }
      record.push(cell);
      records.push(record);
      record = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  if (cell !== '' || record.length > 0) {
    record.push(cell);
    records.push(record);
  }
  return records;
};

/** The headers only the YunExpress bulk sheet has, matched as the server matches them. */
const YUNEXPRESS_SIGNATURE = ['customerorderno.', 'sku1'];

/**
 * The YunExpress bulk-order sheet the order team books portal parcels from (SSD-7457). Its columns are its own, which
 * the server reads by name, so it is sent as it was read: laid over the template it would keep only City and CountryCode.
 */
export const isYunExpressBulkSheet = (csv: string): boolean => {
  const [header = []] = parseCsv(csv.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] ?? '');
  const names = new Set(header.map((name) => name.trim().toLowerCase()));
  return YUNEXPRESS_SIGNATURE.every((name) => names.has(name));
};

const csvCell = (value: string): string => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/** Spreadsheets show thousands with a separator, and a cell formatted that way exports as "1,000". */
const GROUPED_WHOLE_NUMBER = /^\d{1,3}(,\d{3})+$/;

/** The only number the file carries; every other column is text the server reads as written. */
const QTY_COLUMN = 'qty';

/**
 * Lays the desk's file over the server's template before it is previewed: columns are found by their
 * header name, so a sheet with notes or helper columns beside the template ones still reads, and a
 * qty Excel formatted as "1,000" reads as 1000. Blank rows below the data are dropped, and a blank row
 * between data rows is sent as a row of empty cells, which the server counts (it skips an empty
 * line), so the row numbers it reports still match the sheet. A template column the file does not have is
 * left out rather than sent empty: the server reads by header, says which required column a header lacks,
 * and takes a file saved from the original eleven-column template as it is (SSD-7448). Anything else
 * — an unknown country, a bad sku, a missing state — is left for the server to name by row, and a file
 * that already matches the template is sent exactly as read so its hash still recognises a re-upload.
 */
export const alignToTemplate = (csv: string, template: string): string => {
  const [templateLine = ''] = template.split(/\r?\n/);
  const columns = templateLine
    .split(',')
    .map((column) => column.trim())
    .filter(Boolean);
  const text = csv.replace(/^\uFEFF/, '');
  const [header, ...rows] = parseCsv(text);
  if (columns.length === 0 || !header) {
    return csv;
  }
  const names = header.map((name) => name.trim().toLowerCase());
  const found = columns.flatMap((column) => {
    const position = names.indexOf(column.toLowerCase());
    return position < 0 ? [] : [{ column, position }];
  });
  if (found.length === 0) {
    return csv;
  }
  const present = found.map((entry) => entry.column);
  const positions = found.map((entry) => entry.position);
  const qtyIndex = present.indexOf(QTY_COLUMN);
  let changed =
    header.length !== present.length ||
    positions.some((position, index) => position !== index || header[position] !== present[index]);
  const blank = rows.map((cells) => cells.every((cell) => cell.trim() === ''));
  let end = rows.length;
  while (end > 0 && blank[end - 1]) {
    end -= 1;
  }
  // Rows of empty cells below the data are formatted cells rather than rows; the server would name each.
  changed ||= rows.slice(end).some((cells) => cells.length > 1);
  const aligned = rows.slice(0, end).map((cells, row) => {
    if (blank[row]) {
      // The server skips an empty line without counting it, so a blank row between data rows goes
      // as empty cells to keep the rows after it on the row number the sheet shows.
      changed ||= cells.length !== present.length || cells.some((cell) => cell !== '');
      return present.map(() => '');
    }
    return positions.map((position, index) => {
      const value = cells[position] ?? '';
      if (index === qtyIndex && GROUPED_WHOLE_NUMBER.test(value.trim())) {
        changed = true;
        return value.trim().replace(/,/g, '');
      }
      return value;
    });
  });
  if (!changed) {
    return csv;
  }
  return [present, ...aligned].map((cells) => cells.map(csvCell).join(',')).join('\n');
};

/**
 * The server owns the column order and refuses a file whose header differs, so the workbook's only
 * row is the header it hands back. The first sheet is the one an upload reads.
 */
export const marketingImportTemplateWorkbook = (template: string): XLSX.WorkBook => {
  const [headerLine = ''] = template.split(/\r?\n/);
  const header = headerLine.split(',').map((column) => column.trim());
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.aoa_to_sheet([header]), 'Marketing Import');
  return book;
};

export const downloadMarketingImportTemplate = (template: string): void => {
  XLSX.writeFile(marketingImportTemplateWorkbook(template), MARKETING_IMPORT_TEMPLATE_FILENAME);
};
